<script lang="ts">
	import { Dropdown, DropdownItem } from "flowbite-svelte";
	import { Search } from "flowbite-svelte-v1";
	import { getContext, onMount } from "svelte";
	import { writable, type Readable, type Writable } from "svelte/store";
	import { twJoin, twMerge, type ClassNameValue } from "tailwind-merge";
	import type { Suite } from "#gen/bundles/v1/bundle_pb.js";
	import { Order, PaginationArgumentsSchema } from "#gen/common/v1/pagination_pb.js";
	import type { Workspace } from "#gen/identities/v1/identities_pb.js";
	import { PolicyFilterSchema, type Policy } from "#gen/legal/v1/legal_pb.js";
	import { DEFAULT_PAGE_SIZE } from "#lib/api/api.js";
	import { Visibilities } from "#lib/api/bundles.js";
	import type { LegalClient, PolicyList } from "#lib/api/legal.js";
	import Badge from "#lib/components/Badge.svelte";
	import Icon from "#lib/components/Icon.svelte";
	import InfiniteScroll from "#lib/components/InfiniteScroll.svelte";
	import P from "#lib/components/P.svelte";
	import type { PopFn, PushFn } from "#lib/components/StackController.svelte";
	import PolicyEditor from "#lib/components/policy/PolicyEditor.svelte";
	import { DEFAULT_DEBOUNCE_DURATION, debounce } from "#lib/debounce.js";
	import { autofocus } from "#lib/directives/autofocus.js";
	import { Logger } from "#lib/logging.js";
	import PermissionGate from "#lib/permissions/PermissionGate.svelte";
	import { create } from "#lib/protobuf-compat.js";
	import Button from "#lib/ui/Button/index.js";

	interface Props {
		selectedPolicy?: Policy;
		color?: "base" | "green" | "red";
		dropdownClass?: ClassNameValue;
		onselect?: (policy: Policy) => void;
	}

	const legalClient: LegalClient = getContext("legalClient");
	const suite: Writable<Suite> = getContext("suite");
	const workspace: Writable<Workspace> = getContext("workspace");
	const permissions: Readable<Set<string>> = getContext("permissions");
	const push: PushFn = getContext("push");
	const pop: PopFn = getContext("pop");
	const debouncer = debounce(search, DEFAULT_DEBOUNCE_DURATION);

	const dropdownToggleButtonId = "policy-selector";
	const dropdownMenuId = "policy-selector-menu";
	const borderClasses = {
		base: "border-gray-300 group-has-[[contenteditable]:focus]:border-primary-500",
		green: "border-green-500",
		red: "border-red-500",
	};
	const ringClasses = {
		base: "has-[[contenteditable]:focus]:border-primary-500 has-[[contenteditable]:focus]:ring-primary-500 dark:has-[[contenteditable]:focus]:border-primary-500 dark:has-[[contenteditable]:focus]:ring-primary-500",
		green: "focus-within:ring-green-500 focus-within:border-green-500 dark:focus-within:border-green-500 dark:focus-within:ring-green-500",
		red: "focus-within:ring-red-500 focus-within:border-red-500 dark:focus-within:ring-red-500 dark:focus-within:border-red-500",
	};
	const colorClasses = {
		base: "bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400",
		green: "bg-green-50 text-green-900 placeholder-green-700 dark:text-green-400 dark:placeholder-green-500 dark:bg-gray-700",
		red: "bg-red-50 text-red-900 placeholder-red-700 dark:text-red-500 dark:placeholder-red-500 dark:bg-gray-700",
	};

	let { selectedPolicy, color = "base", dropdownClass, onselect }: Props = $props();

	const policies: Writable<PolicyList> = writable({ policies: [], pageInfo: undefined });
	let searchTerm: string | undefined = $state();
	let open = $state(false);

	onMount(async () => {
		update();
	});

	$effect(() => {
		if (searchTerm !== undefined) {
			update();
		}
	});

	let selection = $state(-1);
	function nextSelection() {
		if (selection + 1 < $policies.policies.length) {
			selection += 1;
		}
	}
	function prevSelection() {
		if (selection > 0) {
			selection -= 1;
		}
	}

	let controller: AbortController | undefined = undefined;

	async function update() {
		selection = -1;
		controller?.abort();
		controller = new AbortController();
		await fetchPolicies(searchTerm, false, controller.signal);
		selection = 0;
	}

	async function fetchPolicies(term?: string, scroll = false, signal?: AbortSignal) {
		try {
			const res = await legalClient.listPolicies(
				$suite.orgId,
				create(PolicyFilterSchema, {
					namePrefix: term,
					workspaceId: $suite.workspaceId,
					visibilities: [Visibilities.ORG, Visibilities.PRIVATE],
				}),
				create(PaginationArgumentsSchema, {
					first: DEFAULT_PAGE_SIZE,
					after: scroll ? $policies.pageInfo?.endCursor : undefined,
					order: Order.ASC,
					orderBy: "name",
				}),
				{ signal },
			);
			if (scroll) {
				const newList = $policies.policies;
				newList.push(...res.policies);
				$policies.policies = newList;
			} else {
				$policies.policies = res.policies;
			}
			$policies.pageInfo = res.pageInfo;
		} catch (err) {
			Logger.error("Failed to fetch policies", { err });
		}
	}

	async function keyDown(event: KeyboardEvent) {
		switch (event.code) {
			case "Escape":
				event.stopImmediatePropagation();
				reset();
				break;
			case "ArrowUp":
				event.stopImmediatePropagation();
				prevSelection();
				scrollIntoView($policies.policies[selection]);
				break;
			case "ArrowDown":
				event.stopImmediatePropagation();
				nextSelection();
				scrollIntoView($policies.policies[selection]);
				break;
			case "Enter":
				event.stopImmediatePropagation();
				if (selection < 0 || selection >= $policies.policies.length) break;
				handleSelect($policies.policies[selection]);
				break;
			default:
				selection = 0;
		}
	}

	function handleToggle(event: CustomEvent<boolean>) {
		open = event.detail;
		if (!open && searchTerm !== undefined) {
			reset();
		}
	}

	function handleSelect(policy: Policy) {
		onselect?.(policy);
		reset();
	}

	function handleInput(event: Event) {
		const value = (event.target as HTMLInputElement).value;
		debouncer(value);
	}

	function search(value: string) {
		searchTerm = value;
	}

	function clearSearch() {
		searchTerm = "";
	}

	function scrollIntoView(policy: Policy | undefined) {
		if (!policy) {
			return;
		}
		const element = document.getElementById(policy.id);
		if (element) {
			element.scrollIntoView({ block: "nearest", behavior: "smooth" });
		}
	}

	function onCreateNewPolicy(event: MouseEvent) {
		event.preventDefault();
		event.stopPropagation();
		reset();
		push(PolicyEditor, {
			props: {
				open: true,
				showCreateAndUse: true,
				orgId: $suite.orgId,
				workspace: $workspace,
				class: "p-6",
				onClose: (policy?: Policy) => {
					if (policy && typeof onselect === "function") {
						onselect(policy);
					}
					pop();
					update();
				},
			},
		});
	}

	function reset() {
		searchTerm = "";
		selection = -1;
		open = false;
	}
</script>

<div class="relative flex flex-col gap-1">
	<Button data-action-name="policy-selector-toggle" color="light" class={twJoin("justify-between gap-1.5", borderClasses[color], ringClasses[color], colorClasses[color])}>
		<div class="flex flex-nowrap items-center gap-3">
			{#if selectedPolicy}
				{#if selectedPolicy.content?.content.case == "text"}
					<Icon name="document-text" class="h-5 w-5 flex-none text-gray-400" />
				{:else if selectedPolicy.content?.content.case == "url"}
					<Icon name="link" class="h-5 w-5 flex-none text-gray-400" />
				{/if}
				<div class="flex flex-col">
					<P weight="medium" size="sm" color="text-gray-900">{selectedPolicy.name}</P>
					<P size="sm" secondary>{selectedPolicy.description}</P>
				</div>
			{:else}
				<P weight="medium" size="sm" color="text-gray-400">Select policy...</P>
			{/if}
		</div>
		<Icon name="chevron-down" class="h-4 flex-none" />
	</Button>
	<Dropdown id={dropdownMenuId} bind:open containerClass={twMerge("w-full border border-gray-300 min-w-96 divide-y z-50", dropdownClass)} footerClass={twMerge($permissions.has("adminWorkspace") ? "p-0 border-0" : "")} class="max-h-96 overflow-y-auto" role="menu" aria-labelledby={dropdownToggleButtonId} on:show={handleToggle}>
		<svelte:fragment slot="header">
			<div use:autofocus class="p-3">
				<Search data-action-name="policy-selector-search" size="md" placeholder="Search policies" value={searchTerm} onkeydown={keyDown} oninput={handleInput} clearable classes={{ svg: "text-primary-600" }} clearableOnClick={clearSearch} />
			</div>
		</svelte:fragment>
		{#each $policies.policies as policy, index (policy.id)}
			<DropdownItem data-action-name="policy-selector-item" data-action-title={policy.name} data-policy-id={policy.id} id={policy.id} class={index === selection ? "bg-gray-100 dark:bg-gray-600" : ""} on:click={() => handleSelect(policy)} on:mouseenter={() => (selection = index)}>
				<div class="flex w-full items-center justify-between gap-3">
					<div class="flex items-center gap-3">
						{#if policy.content?.content.case == "text"}
							<Icon name="document-text" class="h-5 w-5 flex-none text-gray-400" />
						{:else if policy.content?.content.case == "url"}
							<Icon name="link" class="h-5 w-5 flex-none text-gray-400" />
						{/if}
						<div class="flex-col">
							<P weight="medium" size="sm" color="text-gray-900" class="pr-2">{policy.name}</P>
							<P size="sm" secondary>{policy.description}</P>
						</div>
					</div>
					{#if policy.visibility === Visibilities.ORG}
						<Badge color="dark">Organization</Badge>
					{/if}
				</div>
			</DropdownItem>
		{:else}
			<DropdownItem>
				<P size="sm" class="text-center">No matching policies</P>
			</DropdownItem>
		{/each}
		<InfiniteScroll hasMore={$policies.pageInfo?.hasNextPage} threshold={0.1} onLoadMore={() => fetchPolicies(searchTerm, true)} />
		<svelte:fragment slot="footer">
			<PermissionGate action="adminWorkspace">
				<DropdownItem class="flex items-center gap-3 rounded-b-lg p-3 text-sm font-medium" data-action-name="create-terms-policy-from-flow-editor" data-action-title="Create New Terms and Conditions Policy from Flow Editor" on:click={onCreateNewPolicy}>
					<Icon name="plus" />
					Create a new terms & conditions policy
				</DropdownItem>
			</PermissionGate>
		</svelte:fragment>
	</Dropdown>
</div>
